<template>
  <div class="jobs-container">
    <DocumentListLoader
      :is-loading="listLoading || pageLoading"
      :number-to-load="4"
      :style="{ width: '100%' }"
    />
    <div
      v-if="!listLoading && !pageLoading"
      :style="{ width: '100%' }"
    >
      <ul class="jobs-list" aria-label="Jobs with revisions for the time period">
        <DpplListItem
          v-for="job in jobs"
          :key="job.id"
          :to="{ name: 'editor-view', params: { id: job.id } }"
          @click.stop.prevent="$router.replace({
            query: { ...$route.query, preview: job.id, type: 'document' },
          })"
        >
          <template #left>
            <div class="left-container">
              <div
                class="score"
              >
                <DocumentScoreLink
                  :id="job.id"
                  :score="job.score_total_capped || 0"
                />
              </div>
              <div class="descriptive-text">
                <div class="d-flex">
                  <span class="job-title">{{ job.title }}</span>
                </div>
                <span class="company-and-location">
                  {{ job.company }}
                  {{ job.location ? `- ${job.location}` : '' }}
                  {{ (job.ats_requisition_id || job.flexible_id) ? `- ${job.ats_requisition_id || job.flexible_id}` : '' }}
                  {{ job.ats_department_name ? `- ${job.ats_department_name}` : '' }}
                </span>
              </div>
            </div>
          </template>
          <template #right>
            <JobBadges
              class="mr-4"
              :is-evergreen="job.is_evergreen"
              :is-internal="!job.is_external && job.documentType === 'job'"
            />
            <div class="right-section">
              <p class="job-metric">
                {{ job.totalRevisions || 'n/a' }}
              </p>
              <span class="p2">Revisions</span>
            </div>
            <div
              class="right-section"
              data-testid="score-change"
            >
              <p
                class="job-metric"
                :class="{ 'is-positive': job.scoreChange > 0, 'is-negative': job.scoreChange < 0 }"
              >
                <span v-if="job.scoreChange > 0">+{{ job.scoreChange }}</span>
                <span v-else-if="job.scoreChange < 0">{{ job.scoreChange }}</span>
                <span v-else>n/a</span>
              </p>
              <span class="p2">Score</span>
            </div>
            <div class="right-section">
              <p class="job-metric">
                {{ job.totalUsers || 'n/a' }}
              </p>
              <span class="p2">Users</span>
            </div>
          </template>
        </DpplListItem>
      </ul>

      <Pagination
        v-if="totalPages > 1"
        :model-value="page"
        :length="totalPages"
        v-bind="{ totalVisible: 20 }"
        @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
      />
    </div>
  </div>
</template>

<script>
import DocumentListLoader from '@/components/library/DocumentListLoader.vue';
import DpplListItem from '@/components/datapeople/DpplListItem.vue';
import { mapState, mapActions } from 'pinia';
import JobBadges from '@/components/editor/badge/JobBadges.vue';
import Pagination from '@/components/shared/Pagination.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppPagination } from '@/stores/app/pagination';
import { useUsage } from '@/stores/insights/usage';
import { useFilters } from '@/stores/filters/filters';
import DocumentScoreLink from '@/components/shared/DocumentScoreLink.vue';

export default {
  name: 'UsageJobs',
  components: {
    DpplListItem,
    Pagination,
    DocumentScoreLink,
    DocumentListLoader,
    JobBadges,
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading', 'listLoading']),
    ...mapState(useAppPagination, ['totalPages', 'page']),
    ...mapState(useUsage, ['jobs']),
  },
  methods: {
    ...mapActions(useFilters, ['sendFilterRequest']),
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.jobs-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border-radius: 8px;

  :deep(.dppl-list-item):last-child {
    border-bottom: none;
  }
}

.jobs-list {
  width: 100%;
  background: @white;
  border-radius: 8px;
  border: 1px solid @stroke-default;
}

.left-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
.score {
  margin: 0px 25px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.descriptive-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.job-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  padding-bottom: 4px;
  text-align: left;
}

.company-and-location {
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  text-align: left;
}

.right-section {
  padding: 5px 24px;
  width: 108px;
  border-left: 1px solid @stroke-default;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.job-metric {
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: @text-brand;

  &.is-positive {
    color: @green-500;
  }

  &.is-negative {
    color: @red-500;
  }
}
</style>
